import { environment } from "@/relay/environment";
import {
	createFileRoute,
	Navigate,
	useLoaderData,
	useParams,
} from "@tanstack/react-router";
import { fetchQuery, graphql } from "react-relay";
import type { cursosNextPageQuery } from "./__generated__/cursosNextPageQuery.graphql";
import type { cursosHeadQuery } from "./__generated__/cursosHeadQuery.graphql";
import { Icon } from "@/components/icon";
import { getImageUrl } from "@/utils/getImageUrl";

export const Route = createFileRoute(
	"/_visibility/_standalone/cursos/$course/",
)({
	component: RouteComponent,
	loader: async ({ params: { course }, context: { club } }) => {
		const data = await fetchQuery<cursosNextPageQuery>(
			environment,
			graphql`
			query cursosNextPageQuery($club: String!, $course: String!) {
			  club(slug: $club) {
					course(slug: $course) {
					  id
						canView
						sections(status: ACTIVE) {
						  classes(status: ACTIVE) {
								slug
								positionInCourse
								canAccess
								completed
							}
						}
					}
				}
			}
			`,
			{ club: club.slug, course },
		).toPromise();

		if (data?.club.course.canView) {
			const sortedLessons = data?.club.course.sections
				.flatMap((s) => s.classes)
				.sort((a, b) => (a.positionInCourse || 0) - (b.positionInCourse || 0));

			const nextLesson = sortedLessons.find((l) => !l.completed && l.canAccess);

			if (nextLesson) {
				return { authorized: true, nextLesson: nextLesson.slug, club };
			}

			const firstNotAvailableLessonIndex = sortedLessons.findIndex(
				(l) => !l.canAccess,
			);

			const nextLessonIndex =
				firstNotAvailableLessonIndex && firstNotAvailableLessonIndex >= 0
					? firstNotAvailableLessonIndex
					: sortedLessons.length;

			const lastAvailableLesson = sortedLessons[nextLessonIndex - 1];

			if (lastAvailableLesson) {
				return { authorized: true, nextLesson: lastAvailableLesson.slug, club };
			}

			return { authorized: true, club };
		} else {
			return { authorized: false, club };
		}
	},
	head: async ({ params: { course }, loaderData }) => {
		if (!loaderData?.club) {
			return {};
		}

		const activeDomain = loaderData.club.domains.find((d) => d.enabled)?.domain;
		const clubUrl = activeDomain
			? `https://${activeDomain}`
			: "https://scarf.club";
		const canonicalUrl = `${clubUrl}/cursos/${course}`;

		const data = await fetchQuery<cursosHeadQuery>(
			environment,
			graphql`
	    query cursosHeadQuery($club: String!, $course: String!) {
	        club(slug: $club) {
						name
	          course(slug: $course) {
							name
							description
							landscapeImage {
								signedUrl
							}
							visibility
							updatedAt
	          }
	        }
	    }
	  `,
			{ club: loaderData.club.slug!, course },
		).toPromise();

		if (!data?.club.course || data.club.course.visibility !== "PUBLIC") {
			return {
				meta: [
					{
						name: "robots",
						content: "noindex,nofollow",
					},
				],
			};
		}

		const ogImageUrl = data.club.course.landscapeImage?.signedUrl
			? getImageUrl(data.club.course.landscapeImage.signedUrl, {
					format: "png",
					width: 1200,
					height: 630,
					quality: 80,
					maxBytes: 600,
				})
			: undefined;

		const description =
			data.club.course.description?.slice(0, 160) ||
			`Course at ${data.club.name}`;

		const structuredData = {
			"@context": "https://schema.org",
			"@type": "Course",
			name: data.club.course.name,
			description: description,
			url: canonicalUrl,
			provider: {
				"@type": "Organization",
				name: data.club.name,
				url: clubUrl,
			},
		};

		return {
			meta: [
				{
					title: `${data.club.course.name} | ${data.club.name}`,
				},
				{
					name: "description",
					content: description,
				},
				{
					name: "canonical",
					content: canonicalUrl,
				},
				{
					property: "og:type",
					content: "website",
				},
				{
					property: "og:title",
					content: `${data.club.course.name} | ${data.club.name}`,
				},
				{
					property: "og:description",
					content: description,
				},
				{
					property: "og:url",
					content: canonicalUrl,
				},
				...(ogImageUrl
					? [
							{
								property: "og:image",
								content: ogImageUrl,
							},
							{
								property: "og:image:secure_url",
								content: ogImageUrl,
							},
							{
								property: "og:image:width",
								content: "1200",
							},
							{
								property: "og:image:height",
								content: "630",
							},
							{
								property: "twitter:image",
								content: ogImageUrl,
							},
						]
					: []),
			],
			links: [
				{
					rel: "canonical",
					href: canonicalUrl,
				},
			],
			scripts: [
				{
					type: "application/ld+json",
					innerHTML: JSON.stringify(structuredData),
				},
			],
		};
	},
});

function RouteComponent() {
	const { course } = useParams({ from: Route.id });
	const { authorized, nextLesson } = useLoaderData({ from: Route.id });

	if (!authorized) {
		return (
			<div className="w-screen h-screen gap-6 flex flex-col items-center justify-center">
				<Icon name="ShieldAlert" className="size-16" />

				<span className="text-balance px-4 md:px-12 lg:px-16 text-center">
					Você não tem acesso a este curso.
					<br />
					Entre em contato com o administrador para obter acesso.
				</span>
			</div>
		);
	}

	if (nextLesson) {
		return (
			<Navigate
				replace
				to="/cursos/$course/$lesson"
				params={{ course, lesson: nextLesson }}
			/>
		);
	}

	return (
		<div className="size-full min-h-full p-6 lg:pt-20 flex flex-col gap-6 items-center justify-center">
			<Icon name="ShieldAlert" className="size-8" />

			<span className="text-center text-balance">
				Este curso ainda não está disponível.
				<br />
				Entre em contato com o administrador para obter mais informações.
			</span>
		</div>
	);
}
